import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { UpsellFlows } from 'src/features/upsellFunnel/screens/UpsellFunnel/components/upsellFlows/UpsellFlows';
import { UpsellLinks } from 'src/features/upsellFunnel/screens/UpsellFunnel/components/upsellLinks/UpsellLinks';

type MenuOption = {
  label: string;
  Component: React.FC;
};

type MenuOptions = Record<string, MenuOption>;

export const OPTIONS_TAB_FUNNEL: MenuOptions = {
  urls: { label: 'Gestão de Urls', Component: UpsellLinks },
  flows: { label: 'Gestão de funis', Component: UpsellFlows },
};

const DEFAULT_TAB_FUNNEL = 'urls';
const TAB_QUERY_PARAM = 'tab';

const getValidTabKey = (tabKey?: string | null) => {
  if (!!tabKey && OPTIONS_TAB_FUNNEL[tabKey]) return tabKey;

  return DEFAULT_TAB_FUNNEL;
};

export function useUpsellFunnel() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabActiveKey, setTabActiveKey] = useState(() =>
    getValidTabKey(
      searchParams.get(TAB_QUERY_PARAM) || location.hash.substring(1)
    )
  );

  useEffect(() => {
    const tabFromQuery = searchParams.get(TAB_QUERY_PARAM);
    const tabFromHash = location.hash.substring(1);
    const nextTabKey = getValidTabKey(tabFromQuery || tabFromHash);

    setTabActiveKey(nextTabKey);

    if (!tabFromQuery && !!tabFromHash && OPTIONS_TAB_FUNNEL[tabFromHash]) {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          params.set(TAB_QUERY_PARAM, tabFromHash);
          return params;
        },
        { replace: true }
      );
    }
  }, [location.hash, searchParams, setSearchParams]);

  const tabActive = useMemo(() => {
    return OPTIONS_TAB_FUNNEL[tabActiveKey];
  }, [tabActiveKey]);

  const labelsMenu = useMemo(
    () =>
      Object.keys(OPTIONS_TAB_FUNNEL).map(
        optionKey => OPTIONS_TAB_FUNNEL[optionKey].label
      ),
    []
  );
  const indexSelectedMenu = useMemo(() => {
    const findTab = Object.entries(OPTIONS_TAB_FUNNEL).findIndex(
      ([key]) => {
        return key === tabActiveKey;
      }
    );
    return `${Math.max(findTab, 0)}`;
  }, [tabActiveKey]);

  const onChangeMenu = useCallback(
    (index: string) => {
      const option = Object.entries(OPTIONS_TAB_FUNNEL)[Number(index)];
      if (!option) return;

      const [tabKey] = option;
      setTabActiveKey(tabKey);
      setSearchParams(prev => {
        const params = new URLSearchParams(prev);
        params.set(TAB_QUERY_PARAM, tabKey);
        return params;
      });
    },
    [setSearchParams]
  );

  return {
    tabActive,
    onChangeMenu,
    labelsMenu,
    indexSelectedMenu,
  };
}
